<script setup>
import { ref, defineProps, defineEmits, computed, watch } from 'vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  produto: {
    type: Object,
    default: () => null
  },
  tiposProduto: {
    type: Array,
    default: () => []
  },
  fabricantes: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:open', 'updated']);

// Form data based on produto prop
const form = ref({
  id: null,
  idrevenda: null,
  fabricante: {
    id: null,
    nome: ''
  },
  tipoproduto: {
    id: null,
    desc: '',
  },
  valor: 0,
  valorportaria: 0,
  statusparcela: 0,
  qtdparcela: 1,
  valor_total_parcelamento: 0, // Added new field
  statusdesconto: 0,
  valordesconto: 0,
  status: 1
});

// Selected values for dropdowns
const selectedFabricante = ref(null);
const selectedTipoProduto = ref(null);

// Loading state
const loading = ref(false);

// Computed properties
const formTitle = computed(() => {
  return form.value.id ? 'Editar Produto' : 'Adicionar Produto';
});

// Is this a new product?
const isNewProduct = computed(() => {
  return !form.value.id;
});

// Watch for changes in the open state of the modal
watch(() => props.open, (isOpen) => {
  // Clear selections when the modal is opening for a new product
  if (isOpen && !props.produto?.id) {
    selectedFabricante.value = null;
    selectedTipoProduto.value = null;
    
    // Reset form to default values for a new product
    form.value = {
      id: null,
      idrevenda: null,
      fabricante: {
        id: null,
        nome: ''
      },
      tipoproduto: {
        id: null,
        desc: '',
      },
      valor: 0,
      valorportaria: 0,
      statusparcela: 0,
      qtdparcela: 1,
      valor_total_parcelamento: 0, // Added new field
      statusdesconto: 0,
      valordesconto: 0,
      status: 1
    };
  }
});

// Copy produto prop to form
watch(() => props.produto, (newVal) => {
  if (newVal) {
    // If it's a new product (no ID), reset selections
    if (!newVal.id) {
      selectedFabricante.value = null;
      selectedTipoProduto.value = null;
    } else {
      // For existing products, set the selections
      if (newVal.fabricante && newVal.fabricante.id) {
        selectedFabricante.value = newVal.fabricante.id.toString();
      }
      
      if (newVal.tipoproduto && newVal.tipoproduto.id) {
        selectedTipoProduto.value = newVal.tipoproduto.id.toString();
      }
    }

    const processedValues = {
      ...JSON.parse(JSON.stringify(newVal)),
      status: newVal.status !== undefined ? Number(newVal.status) : 1,
    };
    
    form.value = { 
      ...form.value,
      ...processedValues
    };
  }
}, { immediate: true });

// Update tipoproduto when selection changes (only when creating new product)
watch(selectedTipoProduto, (newVal) => {
  if (newVal && isNewProduct.value) {
    const tipoProduto = props.tiposProduto.find(tp => tp.value === newVal);
    if (tipoProduto) {
      form.value.tipoproduto.id = parseInt(tipoProduto.value);
    }
  }
});

// Update fabricante when selection changes (only when creating new product)
watch(selectedFabricante, (newVal) => {
  if (newVal && isNewProduct.value) {
    const fabricante = props.fabricantes.find(f => f.value === newVal);
    if (fabricante) {
      form.value.fabricante.id = parseInt(fabricante.value);
    }
  }
});

// Handle form submission
const handleSubmit = async () => {
  loading.value = true;
  try {
    // Form validation
    if (!form.value.valor || form.value.valor <= 0) {
      throw new Error('Valor do produto é obrigatório');
    }
    
    if (isNewProduct.value) {
      if (!form.value.fabricante.id) {
        throw new Error('Fabricante é obrigatório');
      }
      
      if (!form.value.tipoproduto.id) {
        throw new Error('Tipo de produto é obrigatório');
      }
    }
    
    // Emit updated event with form data
    emit('updated', { ...form.value });
    
    // Close modal
    closeModal();
  } catch (error) {
    console.error('Error in edit product form:', error);
    alert(error.message || 'Erro ao salvar produto');
  } finally {
    loading.value = false;
  }
};

// Close modal
const closeModal = () => {
  emit('update:open', false);
};
</script>

<template>
  <v-dialog
    :model-value="open"
    @update:model-value="(val) => emit('update:open', val)"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="text-h5 bg-primary text-white">
        <v-icon start icon="mdi-pencil" class="mr-2"></v-icon>
        {{ formTitle }}
      </v-card-title>
      
      <v-card-text class="pt-4">
        <v-form @submit.prevent="handleSubmit">
          <v-row>
            <!-- Fabricante - Same select for both new and existing products, but readonly when editing -->
            <v-col cols="12">
              <v-select
                v-model="selectedFabricante"
                :items="fabricantes.filter(f => f.value !== '0')"
                item-title="title"
                item-value="value"
                label="Fabricante"
                variant="outlined"
                hide-details="auto"
                class="mb-3"
                :readonly="!isNewProduct"
                :disabled="!isNewProduct"
                required
              ></v-select>
            </v-col>
            
            <!-- Produto - Same select for both new and existing products, but readonly when editing -->
            <v-col cols="12">
              <v-select
                v-model="selectedTipoProduto"
                :items="tiposProduto.filter(tp => tp.value !== '0')"
                item-title="title"
                item-value="value"
                label="Produto"
                variant="outlined"
                hide-details="auto"
                class="mb-3"
                :readonly="!isNewProduct"
                :disabled="!isNewProduct"
                required
              ></v-select>
            </v-col>
            
            <!-- Valor -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.valor"
                label="Valor (dinheiro)"
                type="number"
                variant="outlined"
                prefix="R$"
                step="0.01"
                min="0"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            
            <!-- Valor Portaria -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.valorportaria"
                label="Valor (cartão)"
                type="number"
                variant="outlined"
                prefix="R$"
                step="0.01"
                min="0"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            
            <!-- Parcelamento Toggle -->
            <v-col cols="12" md="6" sm="8">
              <v-switch
                v-model="form.statusparcela"
                :label="`Parcelamento: ${form.statusparcela === 1 ? 'Ativo' : 'Inativo'}`"
                color="primary"
                hide-details="auto"
                class="mb-3"
                :true-value="1"
                :false-value="0"
              ></v-switch>
            </v-col>
            
            <!-- Qtd Parcelas -->
            <v-col cols="12" md="6" sm="4" v-if="form.statusparcela === 1">
              <v-text-field
                v-model.number="form.qtdparcela"
                label="Qtd. máx. parcelas"
                type="number"
                variant="outlined"
                min="1"
                max="12"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            
            <!-- Valor Total Parcelamento - New field -->
            <v-col cols="12" v-if="form.statusparcela === 1">
              <v-text-field
                v-model.number="form.valor_total_parcelamento"
                label="Valor total para parcelamento"
                type="number"
                variant="outlined"
                prefix="R$"
                step="0.01"
                min="0"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            
            <!-- Desconto Toggle -->
            <v-col cols="12" md="6" sm="8">
              <v-switch
                v-model="form.statusdesconto"
                :label="`Desconto no 2º: ${form.statusdesconto === 1 ? 'Ativo' : 'Inativo'}`"
                color="primary"
                hide-details="auto"
                class="mb-3"
                :true-value="1"
                :false-value="0"
              ></v-switch>
            </v-col>
            
            <!-- Valor Desconto -->
            <v-col cols="12" md="6" sm="4" v-if="form.statusdesconto === 1">
              <v-text-field
                v-model.number="form.valordesconto"
                label="Valor do desconto"
                type="number"
                variant="outlined"
                prefix="R$"
                step="0.01"
                min="0"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            
            <!-- Status switch removed as requested -->
          </v-row>
        </v-form>
      </v-card-text>
      
      <v-card-actions class="pb-4 px-4">
        <v-spacer></v-spacer>
        <v-btn
          color="grey-darken-1"
          variant="text"
          @click="closeModal"
          :disabled="loading"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="handleSubmit"
          :loading="loading"
          :disabled="loading"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* Optional styles can go here */
</style>
